/**
 * Which step of starting a table you are on, and what is stopping you.
 *
 * **Pure, and separate from the page** for the reason `characterSteps` is: a
 * wizard's bug and its fix produce identical markup, so a step that never
 * unblocks — or one that unblocks before its choice is real — renders exactly
 * like a working one.
 *
 * ⚠ **One continuous step list.** The table still cannot be entered until it
 * is created, and creation still fails differently from generation — a
 * refused campaign is a validation problem the player can fix, a refused
 * generation is the model being unreachable — but that is enforced by which
 * step's own button fires which network call, not by two separate wizards.
 * `premise`, `map`, `ground` and `world` each keep their own button; only
 * the *visible* list is one thing now.
 *
 * ⚠ **`open`'s `done` used to read `mapped && scaleSet` only** — world
 * generation was optional, so "Go to the table" never actually waited for it,
 * which is the exact bug this whole file exists to close. It is now
 * `seeded && mapped && scaleSet`.
 *
 * ⚠ **`map` comes before `world`.** Generation reads the real, imported
 * terrain to place regions and settlements with actual coordinates — which
 * only works if that terrain exists *before* generation runs. The order
 * used to be inherited from the old, unpositioned-generation design, where
 * the map was purely decorative and nothing cared which came first.
 *
 * ⚠ **`map` is split into `map` and `ground`.** The combined step asked for
 * a picture, a scale, *and* a terrain import in one screen — three tasks,
 * two of them (picture+scale) related and one (the ground) not, all with
 * their own instructions. `map.done` no longer reads `terrainImported`;
 * `ground` is its own step, and its own reason to be blocked.
 *
 * ⚠ **There is no character step.** Making a character and making a
 * campaign have been independent acts since 2026-08-28 — this only ever
 * offered a shortcut, and the shortcut was the thing making the wizard's
 * first screen feel like it was asking the wrong question first.
 * `CampaignWizardState` no longer carries `characterId` for the same reason.
 *
 * ⚠ **There is no `review` step; it is folded into `premise`.** The row
 * still has to be created before `map` — the picture, the terrain import
 * and generation are each their own server write, not form fields, and
 * holding them all in the browser for one final submission would trade away
 * resumability — reopening `.../setup` mid-setup works today because each
 * step already persisted. So creation still happens right after Premise;
 * only the *dedicated recap screen* for it is gone. `premise` fires
 * `POST /api/campaigns` from its own advancing control, which is visually
 * and behaviourally `Wizard`'s own Next, styled identically, labelled
 * "Next", not a green "Create the campaign" button — that it also creates a
 * row is not something a player needs to know. `CampaignWizardState` drops
 * `createdId`: it existed only for `review`'s own `done` check, and no other
 * step ever read it — `map`'s own `done` (which needs a real map upload,
 * which needs a real campaign to upload to) already cannot go true before
 * creation has happened, so nothing here loses its gate by removing the
 * field.
 */

import type { WizardStep } from './Wizard.js';

export interface CampaignWizardState {
  readonly name: string;
  readonly premise: string;
  readonly seeded: boolean;
  readonly mapped: boolean;
  readonly scaleSet: boolean;
  /** Whether a real Azgaar terrain export has been imported — never true for a plain picture. */
  readonly terrainImported: boolean;
}

export function campaignWizardSteps(state: CampaignWizardState): WizardStep[] {
  return [
    {
      key: 'name',
      // ⚠ **Not bare "Name."** The first step sits right where a character
      // used to; a bare "Name" over an unlabelled text box read as the
      // character's, not the table's.
      title: 'Campaign name',
      done: state.name.trim() !== '',
      // One empty text box: the same reason the character wizard's Race,
      // Class, Alignment and Name steps dropped their own "Still needed" —
      // it named nothing the box itself did not.
    },
    {
      key: 'premise',
      title: 'Premise',
      /**
       * ⚠ **Optional to create and required to generate**, which is why it is
       * marked optional and still says so loudly. The premise is the entire
       * input the world generator gets and that run cannot be repeated, so a
       * table created without one can never have a generated world — but
       * refusing to create the campaign would be this page inventing a rule the
       * server does not have. `world` below is now hard-required, so a table
       * made without a premise simply cannot leave that step; the player goes
       * back to fill this in rather than hitting a block at creation time.
       */
      optional: true,
      done: state.premise.trim() !== '',
      // ⚠ **`selfAdvances`, carrying `review`'s old job.** This step's own
      // advancing control fires `POST /api/campaigns` the first time it is
      // pressed (and just moves on if the row already exists, on a resumed
      // session) — the same "this step's button, not a generic Next" shape
      // `map`'s Set and `ground`'s Import already have. The difference is
      // that this one is deliberately styled to be indistinguishable from
      // a plain Next: that it also creates a row is not something a player
      // needs to know.
      selfAdvances: true,
    },
    {
      key: 'map',
      title: 'The map',
      // The picture and its scale only — both are decisions about the same
      // image. The ground is a different task with a different file, from a
      // different menu, and is its own step below.
      done: state.mapped && state.scaleSet,
      blockedBy: [!state.mapped ? 'a world map image' : '', !state.scaleSet ? 'the scale in miles' : '']
        .filter(Boolean)
        .join(' and '),
    },
    {
      key: 'ground',
      title: 'The ground',
      // ⚠ **Its own step, not a third field on `map`.** A plain uploaded
      // picture carries no biome or terrain data at all, so generation cannot
      // read it — only the Azgaar GeoJSON import does, and importing it is
      // unrelated work to uploading a picture.
      done: state.terrainImported,
      ...(!state.terrainImported
        ? { blockedBy: 'the imported ground (Azgaar’s GeoJSON export)' }
        : {}),
    },
    {
      key: 'world',
      title: 'Generate the world',
      // ⚠ **No longer optional** — a table can no longer open on canon written
      // by hand alone. The generator does depend on the model being up, and
      // that is a deliberate tradeoff: an unreachable model now blocks a
      // brand new table from ever opening, not only from having a generated
      // world.
      done: state.seeded,
      ...(!state.seeded ? { blockedBy: 'a generated world for this campaign' } : {}),
    },
    {
      key: 'open',
      title: 'Open the table',
      done: state.seeded && state.mapped && state.scaleSet,
      blockedBy: 'the world, the map and its scale — the steps before this',
    },
  ];
}
